import React, { useState, useMemo } from 'react';
import { useWindowSize } from 'react-use';
import { throttle } from 'lodash';
import { Table, Space, Input, Button } from 'antd';
import { useAtomValue, useSetAtom } from 'jotai';
import { useReadOnlyRole } from '../hooks';
import * as atoms from '../atom';
import { Loading } from '../../common/Loading';
import { Retry } from '../../common/Retry';
import { getColumns, checkIsDisabledRow, checkIsFilterMatched } from './utils';
import { isAllCategory } from '../../utils';
import * as Styled from './styled';

export const SavedFilterList = () => {
  const isSavedFiltersLoading = useAtomValue(atoms.isSavedFiltersLoading);
  const isSavedFiltersError = useAtomValue(atoms.isSavedFiltersError);
  const allFilters = useAtomValue(atoms.savedFilters);
  const dataSource = useAtomValue(atoms.dataSource);
  const user = useAtomValue(atoms.user);
  const disableCreateFilter = useAtomValue(atoms.disableCreateFilter);
  const currentCategory = useAtomValue(atoms.currentCategory);
  const setFilterCreate = useSetAtom(atoms.filterCreate);
  const callbacks = useAtomValue(atoms.callbacks);
  const readOnly = useReadOnlyRole();

  const windowSize = useWindowSize();
  const [text, setText] = useState('');

  const filters = useMemo(() => {
    const categoryStr = `${currentCategory.category}/${currentCategory.subCategory}`;
    const targetFilters = (allFilters || []).filter((it) => {
      const categoryMatched =
        isAllCategory(currentCategory.category) || it.category === categoryStr;
      const filterMatched = checkIsFilterMatched(it, text);
      return categoryMatched && filterMatched;
    });

    return targetFilters;
  }, [allFilters, currentCategory, text]);

  const columns = useMemo(
    () => getColumns({ dataSource, readonly: readOnly, user }),
    [dataSource, readOnly, user],
  );

  const handleSearchTextChange = useMemo(() => {
    const handle = throttle((value: string) => setText(value), 50);
    return handle;
  }, []);

  const handleOnCreateFilter = () => {
    if (disableCreateFilter) return;
    setFilterCreate(true);
  };

  const renderContent = () => {
    if (isSavedFiltersLoading) return <Loading />;
    if (isSavedFiltersError) return <Retry onClick={() => callbacks.onSavedFiltersRetry?.()} />;
    return (
      <Table
        sticky
        style={{
          width: '100%',
          minWidth: '900px',
          overflow: 'auto',
        }}
        dataSource={filters}
        columns={columns}
        pagination={{ pageSize: windowSize.height > 1000 ? 50 : 25, showSizeChanger: false }}
        onRow={(record) => {
          if (checkIsDisabledRow(record, dataSource)) {
            return {
              className: 'disabled-row',
            };
          }
          return {};
        }}
      />
    );
  };

  return (
    <>
      <Styled.TopSection>
        <Styled.SubCategory>
          {isAllCategory(currentCategory.category) ? 'ALL' : currentCategory.subCategory}
        </Styled.SubCategory>
        <Space>
          <Input.Search
            autoFocus
            value={text}
            placeholder="Search"
            allowClear
            style={{ width: 300 }}
            onChange={(e) => handleSearchTextChange(e.target.value)}
            onSearch={handleSearchTextChange}
          />
          {!readOnly && (
            <Button
              type="primary"
              ghost
              disabled={disableCreateFilter}
              onClick={handleOnCreateFilter}
            >
              + New Filter
            </Button>
          )}
        </Space>
      </Styled.TopSection>
      <Styled.TableContainer>{renderContent()}</Styled.TableContainer>
    </>
  );
};
